import React from 'react';
import PropTypes from 'prop-types';
import get from 'lodash.get';
import tracking from 'dibs-tracking';
import { graphql, createPaginationContainer } from 'react-relay';
import CartLayout from './CartLayout';

const trackedProducts = new Set();

class CartContainer extends React.Component {
    constructor(props) {
        super(props);

        this.state = { isLoadingMore: false };
        this.handleLoadMore = this.handleLoadMore.bind(this);
    }

    componentDidMount() {
        this.trackProductImpressions();
    }

    trackProductImpressions() {
        const products = [];
        const portfolioItems = get(this.props, 'viewer.portfolios.edges[0].node.items.edges') || [];

        for (const { node } of portfolioItems) {
            const itemId = get(node, 'item.serviceId');
            const ecommerceTrackingParams = get(node, 'item.ecommerceTrackingParams');
            if (trackedProducts.has(itemId)) {
                continue;
            }
            trackedProducts.add(itemId);
            products.push(ecommerceTrackingParams);
        }

        if (products.length) {
            tracking.trackEcommerce({ type: 'productImpression', products });
        }
    }

    handleLoadMore() {
        if (!this.props.relay.hasMore() || this.props.relay.isLoading()) {
            return;
        }
        this.setState({ isLoadingMore: true }, () => {
            this.props.relay.loadMore(10, () =>
                this.setState({ isLoadingMore: false }, () => this.trackProductImpressions())
            );
        });
    }

    render() {
        const { isLoadingMore } = this.state;
        const { viewer, user } = this.props;
        const portfolioItems = get(viewer, 'portfolios.edges[0].node.items');

        return (
            <React.Fragment>
                <CartLayout
                    viewer={viewer}
                    user={user}
                    portfolioItems={portfolioItems ? portfolioItems : null}
                    onLoadMore={this.handleLoadMore}
                    isLoadingMore={isLoadingMore}
                />
            </React.Fragment>
        );
    }
}

CartContainer.propTypes = {
    relay: PropTypes.object.isRequired,
    viewer: PropTypes.object.isRequired,
    user: PropTypes.object.isRequired,
};

export default createPaginationContainer(
    CartContainer,
    {
        user: graphql`
            fragment CartContainer_user on User {
                ...CartLayout_user
                serviceId
            }
        `,
        viewer: graphql`
            fragment CartContainer_viewer on Viewer
                @argumentDefinitions(
                    count: { type: "Int!", defaultValue: 10 }
                    cursor: { type: "String" }
                ) {
                ...CartLayout_viewer
                portfolios(first: 1, portfolioTypes: [CART], userIds: $userIds) {
                    edges {
                        node {
                            items(first: $count, after: $cursor)
                                @connection(key: "CartItemList_items") {
                                ...CartLayout_portfolioItems
                                totalResults
                                pageInfo {
                                    hasNextPage
                                    endCursor
                                }
                                edges {
                                    node {
                                        item {
                                            serviceId
                                            ecommerceTrackingParams
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        `,
    },

    {
        direction: 'forward',
        getConnectionFromProps(props) {
            return get(props, 'viewer.portfolios.edges[0].node.items');
        },
        getFragmentVariables(prevVars, totalCount) {
            // variables to use for rendering
            return {
                ...prevVars,
                first: totalCount,
            };
        },
        getVariables(props, { count, cursor }) {
            // variables to use for fetching
            const userId = get(props, 'user.serviceId');

            return {
                count,
                cursor,
                userId,
                userIds: [userId],
                hasUserId: !!userId,
            };
        },
        query: graphql`
            query CartContainerPaginationQuery(
                $userId: String!
                $userIds: [String]!
                $count: Int!
                $cursor: String
                $hasUserId: Boolean!
            ) {
                viewer {
                    ...CartContainer_viewer @arguments(count: $count, cursor: $cursor)
                }
            }
        `,
    }
);
